import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TranslateIcon from '@mui/icons-material/Translate';
import PreviewIcon from '@mui/icons-material/Preview';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import BugReportIcon from '@mui/icons-material/BugReport';
import CampaignIcon from '@mui/icons-material/Campaign';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

// Map icon names to components
const iconMap = {
  AutoAwesome: AutoAwesomeIcon,
  TrendingUp: TrendingUpIcon,
  BugReport: BugReportIcon,
  Campaign: CampaignIcon,
};

function NewsAdminPanel({ open, onClose }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0); // 0 = list, 1 = edit
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItem, setMenuItem] = useState(null);

  // Form state
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    category_id: '',
    title_sv: '',
    summary_sv: '',
    content_sv: '',
    title_en: '',
    summary_en: '',
    content_en: '',
    is_published: false,
  });

  // Load news and categories
  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [newsRes, categoriesRes] = await Promise.all([
        api.get('/news/admin/list'),
        api.get('/news/categories'),
      ]);
      setNews(newsRes.data.news);
      setCategories(categoriesRes.data.categories);
    } catch (err) {
      console.error('Failed to load news data:', err);
      setError(t('news.admin.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    if (newValue === 0) {
      resetForm();
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      category_id: categories[0]?.id || '',
      title_sv: '',
      summary_sv: '',
      content_sv: '',
      title_en: '',
      summary_en: '',
      content_en: '',
      is_published: false,
    });
    setError(null);
    setSuccess(null);
  };

  const handleNewClick = () => {
    resetForm();
    setFormData(prev => ({
      ...prev,
      category_id: categories[0]?.id || '',
    }));
    setTab(1);
  };

  const handleEditClick = async (item) => {
    setLoading(true);
    try {
      const res = await api.get(`/news/admin/${item.id}`);
      const newsItem = res.data.news;
      setEditingId(newsItem.id);
      setFormData({
        category_id: newsItem.category_id || '',
        title_sv: newsItem.title_sv || '',
        summary_sv: newsItem.summary_sv || '',
        content_sv: newsItem.content_sv || '',
        title_en: newsItem.title_en || '',
        summary_en: newsItem.summary_en || '',
        content_en: newsItem.content_en || '',
        is_published: newsItem.is_published || false,
      });
      setTab(1);
    } catch (err) {
      console.error('Failed to load news item:', err);
      setError(t('news.admin.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewTranslation = async () => {
    if (!formData.title_sv || !formData.content_sv) {
      setError(t('news.admin.fillSwedishFirst'));
      return;
    }

    setTranslating(true);
    setError(null);
    try {
      const res = await api.post('/news/admin/preview-translation', {
        title_sv: formData.title_sv,
        summary_sv: formData.summary_sv,
        content_sv: formData.content_sv,
      });
      setFormData(prev => ({
        ...prev,
        title_en: res.data.title_en || '',
        summary_en: res.data.summary_en || '',
        content_en: res.data.content_en || '',
      }));
      setSuccess(t('news.admin.translationComplete'));
    } catch (err) {
      console.error('Translation failed:', err);
      setError(err.response?.data?.message || t('news.admin.translationFailed'));
    } finally {
      setTranslating(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title_sv || !formData.content_sv) {
      setError(t('news.admin.titleContentRequired'));
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingId) {
        // Update existing
        await api.put(`/news/admin/${editingId}`, formData);
        setSuccess(t('news.admin.updated'));
      } else {
        // Create new
        await api.post('/news/admin', formData);
        setSuccess(t('news.admin.created'));
      }
      await loadData();
      setTab(0);
      resetForm();
    } catch (err) {
      console.error('Failed to save news:', err);
      setError(err.response?.data?.message || t('news.admin.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (id) => {
    try {
      await api.post(`/news/admin/${id}/publish`);
      await loadData();
      setSuccess(t('news.admin.published'));
    } catch (err) {
      console.error('Failed to publish:', err);
      setError(t('news.admin.publishFailed'));
    }
  };

  const handleUnpublish = async (id) => {
    try {
      await api.post(`/news/admin/${id}/unpublish`);
      await loadData();
      setSuccess(t('news.admin.unpublished'));
    } catch (err) {
      console.error('Failed to unpublish:', err);
      setError(t('news.admin.unpublishFailed'));
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/news/admin/${id}`);
      await loadData();
      setDeleteConfirm(null);
      setSuccess(t('news.admin.deleted'));
    } catch (err) {
      console.error('Failed to delete:', err);
      setError(t('news.admin.deleteFailed'));
    }
  };

  const handleClose = () => {
    resetForm();
    setTab(0);
    onClose();
  };

  // Get icon component for category
  const getCategoryIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || CampaignIcon;
    return <IconComponent fontSize="small" />;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        {t('news.admin.title')}
        <IconButton 
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab label={t('news.admin.list')} />
          <Tab label={editingId ? t('news.admin.edit') : t('news.admin.create')} />
        </Tabs>
      </Box>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {tab === 0 ? (
          // List view
          <Box>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleNewClick}
              >
                {t('news.admin.createNew')}
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : news.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                {t('news.admin.noNews')}
              </Typography>
            ) : (
              <Box>
                {news.map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      mb: 1,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 2,
                      position: 'relative',
                    }}
                  >
                    <IconButton 
                      size="small"
                      onClick={(e) => {
                        setMenuAnchor(e.currentTarget);
                        setMenuItem(item);
                      }}
                      sx={{ 
                        position: 'absolute',
                        right: 8,
                        top: 8
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                    
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, pr: 4 }}>
                      <Box sx={{ color: 'primary.main', mt: 0.5 }}>
                        {getCategoryIcon(item.category_icon)}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                          {item.title_sv}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Chip 
                            label={item.is_published ? t('news.admin.published') : t('news.admin.draft')}
                            size="small"
                            color={item.is_published ? 'success' : 'default'}
                            variant="outlined"
                          />
                          <Chip
                            label={t(`news.admin.translation.${item.translation_status}`)}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {t('news.admin.createdAt')}: {formatDate(item.created_at)}
                          {item.published_at && ` | ${t('news.admin.publishedAt')}: ${formatDate(item.published_at)}`}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* Actions Menu */}
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => {
                setMenuAnchor(null);
                setMenuItem(null);
              }}
            >
              {menuItem?.is_published ? (
                <MenuItem onClick={() => {
                  handleUnpublish(menuItem.id);
                  setMenuAnchor(null);
                  setMenuItem(null);
                }}>
                  <ListItemIcon><UnpublishedIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>{t('news.admin.unpublish')}</ListItemText>
                </MenuItem>
              ) : (
                <MenuItem onClick={() => {
                  handlePublish(menuItem.id);
                  setMenuAnchor(null);
                  setMenuItem(null);
                }}>
                  <ListItemIcon><PublishIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText>{t('news.admin.publish')}</ListItemText>
                </MenuItem>
              )}
              <MenuItem onClick={() => {
                handleEditClick(menuItem);
                setMenuAnchor(null);
                setMenuItem(null);
              }}>
                <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                <ListItemText>{t('common.edit')}</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => {
                setDeleteConfirm(menuItem);
                setMenuAnchor(null);
                setMenuItem(null);
              }} sx={{ color: 'error.main' }}>
                <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText>{t('common.delete')}</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        ) : (
          // Edit/Create view
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>{t('news.admin.category')}</InputLabel>
              <Select
                value={formData.category_id}
                onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                label={t('news.admin.category')}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getCategoryIcon(cat.icon)}
                      {cat.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider>
              <Chip label={t('news.admin.swedish')} />
            </Divider>

            <TextField
              label={t('news.admin.titleSv')}
              value={formData.title_sv}
              onChange={(e) => setFormData(prev => ({ ...prev, title_sv: e.target.value }))}
              fullWidth
              required
            />

            <TextField
              label={t('news.admin.summarySv')}
              value={formData.summary_sv}
              onChange={(e) => setFormData(prev => ({ ...prev, summary_sv: e.target.value }))}
              fullWidth
              multiline
              rows={2}
              helperText={t('news.admin.summaryHelp')}
            />

            <TextField
              label={t('news.admin.contentSv')}
              value={formData.content_sv}
              onChange={(e) => setFormData(prev => ({ ...prev, content_sv: e.target.value }))}
              fullWidth
              multiline
              rows={8}
              required
              helperText={t('news.admin.markdownSupported')}
            />

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={translating ? <CircularProgress size={16} /> : <TranslateIcon />}
                onClick={handlePreviewTranslation}
                disabled={translating || !formData.title_sv || !formData.content_sv}
              >
                {translating ? t('news.admin.translating') : t('news.admin.translateToEnglish')}
              </Button>
            </Box>

            <Divider>
              <Chip label={t('news.admin.english')} />
            </Divider>

            <TextField
              label={t('news.admin.titleEn')}
              value={formData.title_en}
              onChange={(e) => setFormData(prev => ({ ...prev, title_en: e.target.value }))}
              fullWidth
            />

            <TextField
              label={t('news.admin.summaryEn')}
              value={formData.summary_en}
              onChange={(e) => setFormData(prev => ({ ...prev, summary_en: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />

            <TextField
              label={t('news.admin.contentEn')}
              value={formData.content_en}
              onChange={(e) => setFormData(prev => ({ ...prev, content_en: e.target.value }))}
              fullWidth
              multiline
              rows={8}
            />

            <Divider />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_published}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_published: e.target.checked }))}
                />
              }
              label={t('news.admin.publishImmediately')}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {tab === 1 ? (
          <>
            <Button onClick={() => setTab(0)}>
              {t('common.cancel')}
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : null}
            >
              {saving ? t('common.saving') : t('common.save')}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose}>
            {t('common.close')}
          </Button>
        )}
      </DialogActions>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>{t('news.admin.deleteConfirm.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('news.admin.deleteConfirm.message', { title: deleteConfirm?.title_sv })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>
            {t('common.cancel')}
          </Button>
          <Button 
            color="error" 
            variant="contained"
            onClick={() => handleDelete(deleteConfirm?.id)}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default NewsAdminPanel;
